import { useState, useEffect } from 'react';
import { Building2, CheckCircle2, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

export default function PlaidConnect() {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);

  const createLinkToken = async () => {
    try {
      const data = await apiFetch<{ link_token: string }>('/api/plaid/link-token', { method: 'POST' });
      setLinkToken(data.link_token);
    } catch (error) {
      console.error('Failed to create link token:', error);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    
    // In a real implementation, this would use the Plaid Link SDK
    // For now, we'll simulate the connection
    setTimeout(async () => {
      try {
        // Simulate public token exchange
        await fetch('/api/plaid/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ public_token: 'simulated-public-token' }),
        });
        
        setConnected(true);
      } catch (error) {
        console.error('Connection failed:', error);
      } finally {
        setLoading(false);
      }
    }, 2000);
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const data = await apiFetch<{ connected: boolean }>('/api/plaid/accounts');
        setConnected(data.connected);
      } catch (error) {
        console.error('Failed to check connection:', error);
      }
    };
    
    checkConnection();
  }, []);

  if (connected) {
    return (
      <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-lg p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Bank Connected</h3>
            <p className="text-sm text-slate-400">Plaid integration active</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 bg-slate-700/50 rounded-full flex items-center justify-center">
          <Building2 className="w-6 h-6 text-slate-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Connect Bank Account</h3>
          <p className="text-sm text-slate-400">Link your bank via Plaid</p>
        </div>
      </div>

      <button
        onClick={linkToken ? handleConnect : createLinkToken}
        disabled={loading}
        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Connecting...
          </>
        ) : (
          'Connect with Plaid'
        )}
      </button>
    </div>
  );
}
